import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { AppButton } from '@app/shared/components/AppButton';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { Moment } from 'moment';
import Switch from '@material-ui/core/Switch';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSelector, useDispatch } from 'react-redux';
import {
  getCOCurrencySelector,
  getProductsName,
} from '@app/store/baseMargin/selectors';
import {
  fetchProductsName,
  fetchExceptionName,
  createExceptionAction,
} from '@app/store/baseMargin/actions';
import {
  CreateException,
  IndustriesData,
  ProductNameData,
} from '@app/shared/types/baseMargin';

import { ProductGroupsLevel } from './components/ProductGroupsLevel';
import { IndustriesLevels } from './components/IndustriesLevels';
import { schema } from './utils/validation';
import { validate } from '@app/shared/utils/validate';
import { selectSelectedCo } from '@app/store/filters-module/co';
import BaseMarginService from '@services/base-margin-service/baseMarginService';
import Typography from '@material-ui/core/Typography';
import styles from './ConfigureDialog.module.scss';

interface Props {
  configureOpen: boolean;
  handleClickOpen: () => void;
  handleClose: () => void;
}

const mapAllocation: Record<number | string, string> = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
};
interface DataModal {
  exceptionProductLevel: number;
  exceptionIndustryLevel: number;
  reminder: boolean;
  productValue: { key: number; value: string }[];
  validFrom: null | Date | any;
  validTo: null | Date | any;
  description: string;
  margin: string;
  exceptionName: string;
}
export const ConfigureDialog: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const productsNameStore = useSelector(getProductsName);
  const baseMartrixCO = useSelector(selectSelectedCo);
  const coCurrency = useSelector(getCOCurrencySelector);

  const currentCurrency = useMemo(() => {
    const currentCO = coCurrency.find((item) => item.id === baseMartrixCO?.id);
    return currentCO;
  }, [coCurrency, baseMartrixCO]);

  useEffect(() => {
    dispatch(fetchExceptionName({}));
  }, [dispatch]);

  const [data, setData] = useState<DataModal>({
    exceptionProductLevel: 1,
    exceptionIndustryLevel: 1,
    reminder: false,
    productValue: [],
    validFrom: null,
    validTo: null,
    description: '',
    margin: '',
    exceptionName: '',
  });

  const [formError, setFormError] = useState<Record<string, string>>();

  const [productGroupsLevel, setProductGroupsLevel] = useState<
    Record<number, undefined | { id: string; name: string }[]>
  >({ 1: [], 2: [], 3: [], 4: [], 5: [] });

  const [industriesLevel, setIndustriesLevel] = useState<
    Record<number, undefined | IndustriesData[]>
  >({ 1: [], 2: [], 3: [], 4: [] });

  const [carrencyValue, setCurrencyValue] = useState<number | undefined>(
    currentCurrency?.coCurrencies[0].key
  );

  const [productsName, setProductsName] = useState<ProductNameData[]>([]);
  const [productsNameLoading, setProductsNameLoading] = useState(false);
  const [currentProductsName, setCurrentProductsName] = useState<
    ProductNameData[] | undefined
  >([]);
  const [currentProductAllocation, setCurrentProductAllocation] = useState<
    { key: number; value: string }[]
  >([]);
  const [productLoading, setProductLoading] = useState(false);
  useEffect(() => {
    if (productsNameStore.length) {
      setProductsName(productsNameStore);
    }
    setProductsNameLoading(false);
  }, [productsNameStore]);
  useEffect(() => {
    setProductLoading(false);
  }, [currentProductAllocation]);
  const getValueAllocation = async () => {
    setProductLoading(true);
    const level =
      data.exceptionProductLevel === 6 ? 5 : data.exceptionProductLevel;
    const dataToFetch = productGroupsLevel[level];
    if (dataToFetch && baseMartrixCO) {
      const result: {
        data: string[];
      } = await BaseMarginService.getAllocationLevel(
        baseMartrixCO.id,
        dataToFetch.map((i) => i.id),
        level
      );

      const newAllocation = result.data.map((i) => {
        return {
          key: +i,
          value: mapAllocation[i],
        };
      });
      setCurrentProductAllocation(newAllocation);
    }
  };
  const handleChangeInput = useCallback((event: any) => {
    setData((state: any) => {
      return {
        ...state,
        [event.target.name]: event.target.value,
      };
    });
  }, []);
  const handleChangeSelect = useCallback((event: any) => {
    setData((state: any) => {
      return {
        ...state,
        [event.target.name]: event.target.value,
      };
    });
  }, []);

  const handleProductValue = useCallback(
    (dataAllocation: { key: number; value: string }[] | null) => {
      const findAllocation = dataAllocation?.some((item) => item.key !== 0);
      const findAllAllocation = dataAllocation?.findIndex(
        (item) => item.key === 0
      );

      if (!dataAllocation?.length) {
        setData((state: any) => {
          return {
            ...state,
            productValue: [],
          };
        });
        return;
      }
      if (data.productValue.length === 0) {
        setData((state: any) => {
          return {
            ...state,
            productValue: dataAllocation,
          };
        });
      }

      if (findAllAllocation !== 0 && findAllAllocation !== -1) {
        setData((state: any) => {
          return {
            ...state,
            productValue: [{ key: 0, value: 'All' }],
          };
        });
        return;
      }
      if (findAllocation && dataAllocation) {
        const newDate = dataAllocation.filter((item: any) => item.key !== 0);
        setData((state: any) => {
          return {
            ...state,
            productValue: newDate,
          };
        });
        return;
      }
    },
    [data.productValue]
  );

  const handleChangeReminder = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData((state: any) => {
        return {
          ...state,
          reminder: event.target.checked,
        };
      });
    },
    []
  );

  const handleChangeDate = useCallback(
    (date: Moment | null, isFrom: boolean) => {
      let parseDate: Date = date?.toDate() as Date;
      parseDate.setHours(0, 0, 0, 0);
      if (isFrom) {
        setData((state: any) => {
          return {
            ...state,
            validFrom: parseDate,
          };
        });
        return;
      }
      if (!isFrom && data.validFrom <= parseDate) {
        parseDate.setHours(23, 59, 59, 999)
        setData((state: any) => {
          return {
            ...state,
            validTo: parseDate,
          };
        });
      }
    },
    [data.validFrom]
  );

  const onProductGroups = useCallback(
    (data: { id: string; name: string } | undefined) => {
      setData((state: DataModal) => {
        return {
          ...state,
          productValue: [],
        };
      });
      setProductGroupsLevel((state: any) => {
        return {
          ...state,
          ...data,
        };
      });
    },
    []
  );
  const onIndustries = useCallback((data: IndustriesData | null) => {
    setIndustriesLevel((state) => {
      return {
        ...state,
        ...data,
      };
    });
  }, []);

  const fetchProductsNameData = useCallback(() => {
    setProductsNameLoading(true);
    dispatch(
      fetchProductsName({
        payload: {
          id: productGroupsLevel[5]?.map((item) => item.id) as string[],
          valueAllocation:
            !data.productValue.length || data.productValue[0].key === 0
              ? [1, 2, 3]
              : data.productValue.map((item: any) => item.key),
        },
      })
    );
  }, [productGroupsLevel, data.productValue, dispatch]);

  const handleProductsName = useCallback(
    (data: ProductNameData[] | undefined) => {
      setCurrentProductsName(data);

      if (!data?.length) {
        setData((prev) => ({ ...prev, productValue: [] }));
      }
    },
    []
  );
  const checkFormIsValid = useCallback(() => {
    const productValueObj = Object.values(productGroupsLevel)
      .slice(0, data.exceptionProductLevel)
      .reduce(
        (
          previousValue: Record<number, { id: string; name: string }[]>,
          currentValue: any,
          index: number
        ) => {
          return {
            ...previousValue,
            [index + 1]: currentValue,
          };
        },
        {} as Record<number, { id: string; name: string }[]>
      );
    const industryValueObj = Object.values(industriesLevel)
      .slice(0, data.exceptionIndustryLevel)
      .reduce(
        (
          previousValue: Record<number, IndustriesData[]>,
          currentValue: any,
          index: number
        ) => {
          return {
            ...previousValue,
            [index + 1]: currentValue,
          };
        },
        {} as Record<number, IndustriesData[]>
      );

    const valid = validate(
      {
        exceptionName: data.exceptionName,
        description: data.description,
        productGroupsLevel: productValueObj,
        industryGroups: industryValueObj,
        marginType: carrencyValue,
        margin: data.margin,
        exceptionProductLevel: data.exceptionProductLevel,
        productsName: currentProductsName,
        valueAllocation: data.productValue,
      },
      schema
    );
    setFormError(valid);
    if (Object.keys(valid).length) {
      return false;
    }
    return true;
  }, [
    data.description,
    data.exceptionProductLevel,
    productGroupsLevel,
    currentProductsName,
    industriesLevel,
    data.exceptionIndustryLevel,
    data.margin,
    carrencyValue,
    data.exceptionName,
    data.productValue,
  ]);
  const createException = () => {
    const valid = checkFormIsValid();
    if (!valid) return;
    const industryId = industriesLevel[
      data.exceptionIndustryLevel
    ] as IndustriesData[];
    const groupId = productGroupsLevel[
      data.exceptionProductLevel === 6 ? 5 : data.exceptionProductLevel
    ] as { id: string; name: string }[];

    const newException: CreateException = {
      margin: data.margin,
      marginType: carrencyValue as number,
      description: data.description,
      validFrom: new Date(data.validFrom).toISOString(),
      validTo: new Date(data.validTo).toISOString(),
      name: data.exceptionName,
      reminder: data.reminder,
      industryIds: industryId.map((item) => item.id),
      groupIds: groupId.map((item) => item.id),
      productIds: currentProductsName
        ? currentProductsName.map((item) => item.id)
        : null,
      valueAllocations: data.productValue.map((i) => i.key),
    };
    dispatch(createExceptionAction({ payload: newException }));
  };

  React.useEffect(() => {
    const isShouldClearChildrenInputs = !productGroupsLevel[
      data.exceptionProductLevel
    ]?.length;

    if (isShouldClearChildrenInputs) {
      setData((prev) => ({ ...prev, productValue: [] }));
      setCurrentProductsName([]);
    }
  }, [data.exceptionProductLevel, productGroupsLevel]);

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <Dialog
        open={props.configureOpen}
        onClose={props.handleClose}
        fullWidth={true}
        maxWidth="lg"
      >
        <DialogTitle>Creation of exception</DialogTitle>
        <DialogContent style={{ marginBottom: '100px' }}>
          <Grid container direction="row">
            <Grid item xs={6} className={styles.rowWrapper}>
              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid xs={4} item>
                  Exception name
                </Grid>
                <Grid xs={8} item>
                  <TextField
                    error={
                      formError && formError['exceptionName'] ? true : false
                    }
                    helperText={
                      formError && formError['exceptionName']
                        ? formError['exceptionName']
                        : ''
                    }
                    name="exceptionName"
                    onChange={handleChangeInput}
                    value={data.exceptionName}
                    fullWidth
                    placeholder="Enter name for this exception"
                  />
                </Grid>
              </Grid>
              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Description
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    error={formError && formError['description'] ? true : false}
                    helperText={
                      formError && formError['description']
                        ? formError['description']
                        : ''
                    }
                    name="description"
                    onChange={handleChangeInput}
                    value={data.description}
                    fullWidth
                    placeholder="Enter description for this exception"
                  />
                </Grid>
              </Grid>

              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Exception product level
                </Grid>
                <Grid item xs={8}>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={data.exceptionProductLevel}
                    onChange={handleChangeSelect}
                    fullWidth
                    name="exceptionProductLevel"
                  >
                    <MenuItem value={1}>On product group level 1</MenuItem>
                    <MenuItem value={2}>On product group level 2</MenuItem>
                    <MenuItem value={3}>On product group level 3</MenuItem>
                    <MenuItem value={4}>On product group level 4</MenuItem>
                    <MenuItem value={5}>On main product</MenuItem>
                    <MenuItem value={6}>On product SKU level</MenuItem>
                  </Select>
                </Grid>
              </Grid>
              <ProductGroupsLevel
                productGroupsLevel={productGroupsLevel}
                onProductGroups={onProductGroups}
                exceptionProductLevel={data.exceptionProductLevel}
                formError={formError}
                isFullChildren={false}
                isFullList={false}
              />
              {data.exceptionProductLevel === 6 ? (
                <>
                  <Grid
                    container
                    alignItems="center"
                    direction="row"
                    spacing={4}
                  >
                    <Grid item xs={4}>
                      Product number / name
                    </Grid>
                    <Grid item xs={8}>
                      <Autocomplete
                        disabled={productGroupsLevel[5]?.length ? false : true}
                        value={currentProductsName || []}
                        loading={productsNameLoading}
                        multiple
                        id={`product-names`}
                        filterOptions={(options, state) => {
                          return options.filter((o) => {
                            return (
                              o.name
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase()) ||
                              o.number
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase())
                            );
                          });
                        }}
                        options={productsName}
                        onChange={(_e, data) => {
                          handleProductsName(data);
                        }}
                        limitTags={2}
                        getOptionLabel={(option) =>
                          `(${option.number}) ${option.name}`
                        }
                        getOptionSelected={(option, value) =>
                          option.id === value.id
                        }
                        onOpen={fetchProductsNameData}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            helperText={
                              formError && formError[`productsName`]
                                ? formError[`productsName`]
                                    .split(' ')
                                    .slice(1)
                                    .join(' ')
                                : false
                            }
                            error={
                              formError && formError[`productsName`]
                                ? true
                                : false
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : null}
              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Product value
                </Grid>
                <Grid item xs={8}>
                  <Autocomplete
                    multiple
                    id={`product-allocation`}
                    options={currentProductAllocation}
                    onChange={(_e, data) => handleProductValue(data)}
                    limitTags={2}
                    value={data.productValue}
                    getOptionLabel={(option) => option.value}
                    getOptionSelected={(option, value) =>
                      option.key === value.key
                    }
                    loading={productLoading}
                    onOpen={getValueAllocation}
                    disabled={
                      data.exceptionProductLevel === 6
                        ? productGroupsLevel[data.exceptionProductLevel - 1]
                            ?.length
                          ? false
                          : true
                        : productGroupsLevel[data.exceptionProductLevel]?.length
                        ? false
                        : true
                    }
                    renderInput={(params) => (
                      <TextField
                        error={
                          formError && formError['valueAllocation']
                            ? true
                            : false
                        }
                        helperText={
                          formError && formError['valueAllocation']
                            ? formError['valueAllocation']
                            : ''
                        }
                        {...params}
                        variant="standard"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} className={styles.rowWrapper}>
              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Exception industry level
                </Grid>
                <Grid item xs={8}>
                  <Select
                    value={data.exceptionIndustryLevel}
                    onChange={handleChangeSelect}
                    fullWidth
                    name="exceptionIndustryLevel"
                  >
                    <MenuItem value={1}>On industry level 1</MenuItem>
                    <MenuItem value={2}>On industry level 2</MenuItem>
                    <MenuItem value={3}>On industry level 3</MenuItem>
                    <MenuItem value={4}>On industry level 4</MenuItem>
                  </Select>
                </Grid>
              </Grid>
              <IndustriesLevels
                exceptionIndustryLevel={data.exceptionIndustryLevel}
                industriesLevel={industriesLevel}
                onIndustries={onIndustries}
                formError={formError}
              />

              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Valid from
                </Grid>
                <Grid item xs={8}>
                  <DatePicker
                    format="DD/MM/yyyy"
                    fullWidth
                    value={data.validFrom}
                    onChange={(date) => handleChangeDate(date, true)}
                    disablePast={true}
                  />
                </Grid>
              </Grid>
              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Valid to
                </Grid>
                <Grid item xs={8}>
                  <DatePicker
                    format="DD/MM/yyyy"
                    fullWidth
                    value={data.validTo}
                    onChange={(date) => handleChangeDate(date, false)}
                    disablePast={true}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                alignItems="flex-start"
                direction="row"
                spacing={4}
              >
                <Grid item xs={4}>
                  Target margin exception
                </Grid>
                <Grid item container xs={8}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      onChange={handleChangeInput}
                      name="margin"
                      value={data.margin}
                      type="number"
                      placeholder=""
                      error={formError && formError['margin'] ? true : false}
                      helperText={
                        formError && formError['margin']
                          ? 'Target margin is a required field'
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {currentCurrency ? (
                      <Select
                        fullWidth
                        value={carrencyValue}
                        onChange={(e) =>
                          setCurrencyValue(e.target.value as number)
                        }
                      >
                        {currentCurrency.coCurrencies.map(
                          (item, index: number) => (
                            <MenuItem key={index} value={item.key}>
                              {item.value}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    ) : null}
                  </Grid>
                  <Typography
                    variant="caption"
                    gutterBottom
                    style={{ color: '#ff5000' }}
                  >
                    When creating an exception for product groups, please make
                    sure that the margin unit corresponds to the one of the
                    default margin of these product groups
                  </Typography>
                </Grid>
              </Grid>

              <Grid container alignItems="center" direction="row" spacing={4}>
                <Grid item xs={4}>
                  Reminder
                </Grid>
                <Grid item xs={6}>
                  <Switch
                    color="primary"
                    checked={data.reminder}
                    onChange={handleChangeReminder}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={props.handleClose} color="default">
            Cancel
          </AppButton>
          <AppButton onClick={createException} color="primary">
            Create
          </AppButton>
        </DialogActions>
      </Dialog>
    </MuiPickersUtilsProvider>
  );
};
